import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS, SHADOWS, STATUS_COLORS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { blueBtnStyle, inputStyle } from '../../styles/admin-styles';
import { TakDeviceRow } from '../../components/TakDeviceRow';
import {
  fetchContacts,
  fetchGroups,
  fetchServerInfo,
  fetchMissions,
} from '../../api/tak-admin';
import type {
  TakContact,
  TakGroup,
  TakServerInfo,
  TakMission,
} from '../../api/tak-admin';

type SubTab = 'overview' | 'devices' | 'groups' | 'missions';

const SUB_TABS: { key: SubTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'devices', label: 'Devices' },
  { key: 'groups', label: 'Groups' },
  { key: 'missions', label: 'Missions' },
];

interface SummaryCardProps {
  label: string;
  value: string | number;
  isDark: boolean;
  colors: ColorTheme;
  accent?: string;
}

function SummaryCard({ label, value, isDark, colors, accent }: SummaryCardProps) {
  return (
    <div
      style={{
        flex: '1 1 180px',
        padding: '20px',
        borderRadius: '8px',
        backgroundColor: colors.card,
        boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          fontFamily: FONTS.heading,
          fontSize: '9px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          marginBottom: '8px',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: FONTS.heading,
          fontSize: '28px',
          color: accent ?? colors.text,
          fontWeight: 400,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** TAK Server admin: overview stats plus device, group, and mission lists. */
export function TakAdmin(): React.JSX.Element {
  const { token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [activeTab, setActiveTab] = useState<SubTab>('overview');
  const [contacts, setContacts] = useState<TakContact[]>([]);
  const [groups, setGroups] = useState<TakGroup[]>([]);
  const [serverInfo, setServerInfo] = useState<TakServerInfo | null>(null);
  const [missions, setMissions] = useState<TakMission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const AUTO_REFRESH_MS = 30_000;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [c, g, s, m] = await Promise.all([
        fetchContacts(token).catch(() => []),
        fetchGroups(token).catch(() => []),
        fetchServerInfo(token).catch(() => ({ version: 'unreachable' })),
        fetchMissions(token).catch(() => []),
      ]);
      setContacts(c);
      setGroups(g);
      setServerInfo(s);
      setMissions(m);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (autoRefresh) {
      refreshTimer.current = setInterval(loadData, AUTO_REFRESH_MS);
    }
    return () => {
      if (refreshTimer.current) {
        clearInterval(refreshTimer.current);
        refreshTimer.current = null;
      }
    };
  }, [autoRefresh, loadData]);

  const filteredContacts = contacts.filter(c => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (c.callsign?.toLowerCase().includes(q))
      || (c.uid?.toLowerCase().includes(q))
      || (c.team?.toLowerCase().includes(q));
  });

  const filteredGroups = groups.filter(g => {
    if (!searchQuery.trim()) return true;
    return g.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredMissions = missions.filter(m => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return m.name.toLowerCase().includes(q)
      || (m.description?.toLowerCase().includes(q));
  });

  const onlineCount = contacts.filter(c =>
    c.lastStatus?.toLowerCase() === 'connected' || c.lastStatus?.toLowerCase() === 'online',
  ).length;

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px',
    borderRadius: '6px',
    border: 'none',
    fontFamily: FONTS.heading,
    fontSize: '11px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    color: active ? '#FFFFFF' : (isDark ? '#D0D0D0' : '#333333'),
    backgroundColor: active
      ? (isDark ? 'rgba(1, 169, 237, 0.15)' : 'rgba(1, 101, 198, 0.1)')
      : 'transparent',
    transition: 'background-color 0.2s',
  });

  const headerRowStyle: React.CSSProperties = {
    display: 'grid',
    gap: '12px',
    padding: '12px 20px',
    fontFamily: FONTS.heading,
    fontSize: '9px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
  };

  return (
    <div>
      {/* Header */}
      <div className="vx-page-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
        <h2 className="vx-track" style={{ fontFamily: FONTS.heading, fontSize: '20px', color: colors.text, margin: 0 }}>
          TAK Server Admin
        </h2>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            className="vx-search-field"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ ...inputStyle(isDark, colors), width: '200px' }}
          />
          <button
            onClick={() => setAutoRefresh(prev => !prev)}
            style={{
              height: '36px',
              padding: '0 14px',
              borderRadius: '6px',
              border: `1px solid ${autoRefresh ? COLORS.accent.blue : colors.border}`,
              backgroundColor: autoRefresh
                ? (isDark ? 'rgba(1, 169, 237, 0.12)' : 'rgba(1, 169, 237, 0.06)')
                : 'transparent',
              color: autoRefresh ? COLORS.accent.blue : colors.textSecondary,
              fontFamily: FONTS.heading,
              fontSize: '10px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Auto {autoRefresh ? 'On' : 'Off'}
          </button>
          <button onClick={loadData} style={blueBtnStyle(isDark, loading)} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '6px',
            backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#EF4444',
            fontFamily: FONTS.body,
            fontSize: '13px',
          }}
        >
          TAK Server error: {error}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="vx-tab-scroller" style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
        {SUB_TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={tabBtnStyle(activeTab === tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading && !contacts.length ? (
        <p style={{ color: colors.textSecondary, fontFamily: FONTS.body }}>Loading TAK data...</p>
      ) : activeTab === 'overview' ? (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <SummaryCard
            label="Connected Devices"
            value={`${onlineCount} / ${contacts.length}`}
            isDark={isDark}
            colors={colors}
            accent={COLORS.accent.blue}
          />
          <SummaryCard
            label="Active Groups"
            value={groups.filter(g => g.active !== false).length}
            isDark={isDark}
            colors={colors}
            accent={COLORS.accent.cyan}
          />
          <SummaryCard
            label="Missions"
            value={missions.length}
            isDark={isDark}
            colors={colors}
            accent={COLORS.accent.gold}
          />
          <SummaryCard
            label="Server Version"
            value={serverInfo?.version ?? 'N/A'}
            isDark={isDark}
            colors={colors}
          />
        </div>
      ) : activeTab === 'devices' ? (
        <div
          style={{
            borderRadius: '8px',
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
            overflow: 'hidden',
          }}
        >
          <div className="vx-desktop-header" style={{ ...headerRowStyle, gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr' }}>
            <span>Callsign</span>
            <span>UID</span>
            <span>Last Seen</span>
            <span>Status</span>
            <span>Team</span>
          </div>
          {filteredContacts.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontFamily: FONTS.body }}>
              {searchQuery ? 'No devices match your search' : 'No connected devices'}
            </div>
          ) : (
            filteredContacts.map(contact => (
              <TakDeviceRow key={contact.uid} contact={contact} isDark={isDark} colors={colors} />
            ))
          )}
        </div>
      ) : activeTab === 'groups' ? (
        <div
          style={{
            borderRadius: '8px',
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
            overflow: 'hidden',
          }}
        >
          <div className="vx-desktop-header" style={{ ...headerRowStyle, gridTemplateColumns: '2fr 1fr 1fr' }}>
            <span>Name</span>
            <span>Direction</span>
            <span>Active</span>
          </div>
          {filteredGroups.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontFamily: FONTS.body }}>
              {searchQuery ? 'No groups match your search' : 'No groups found'}
            </div>
          ) : (
            filteredGroups.map(group => (
              <div
                key={`${group.name}-${group.direction}`}
                className="vx-stack-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: '12px',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${colors.border}`,
                  fontFamily: FONTS.body,
                  fontSize: '13px',
                  color: colors.text,
                }}
              >
                <span className="vx-labeled-cell" data-label="Name" style={{ fontWeight: 500 }}>{group.name}</span>
                <span className="vx-labeled-cell" data-label="Direction" style={{ color: colors.textSecondary }}>{group.direction}</span>
                <span className="vx-labeled-cell" data-label="Active" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: group.active !== false ? STATUS_COLORS.online : STATUS_COLORS.offline,
                    }}
                  />
                  <span style={{ fontSize: '12px', color: group.active !== false ? STATUS_COLORS.online : STATUS_COLORS.offline }}>
                    {group.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </span>
              </div>
            ))
          )}
        </div>
      ) : (
        <div
          style={{
            borderRadius: '8px',
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
            overflow: 'hidden',
          }}
        >
          <div className="vx-desktop-header" style={{ ...headerRowStyle, gridTemplateColumns: '2fr 2fr 1.5fr' }}>
            <span>Name</span>
            <span>Creator</span>
            <span>Created</span>
          </div>
          {filteredMissions.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary, fontFamily: FONTS.body }}>
              {searchQuery ? 'No missions match your search' : 'No missions found'}
            </div>
          ) : (
            filteredMissions.map(mission => (
              <div
                key={mission.name}
                className="vx-stack-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 2fr 1.5fr',
                  gap: '12px',
                  padding: '14px 20px',
                  borderBottom: `1px solid ${colors.border}`,
                  fontFamily: FONTS.body,
                  fontSize: '13px',
                  color: colors.text,
                }}
              >
                <span className="vx-labeled-cell" data-label="Name" style={{ fontWeight: 500 }}>{mission.name}</span>
                <span className="vx-labeled-cell" data-label="Creator" style={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '12px' }}>
                  {mission.creatorUid ?? '—'}
                </span>
                <span className="vx-labeled-cell" data-label="Created" style={{ color: colors.textSecondary, fontSize: '12px' }}>
                  {mission.createTime ? new Date(mission.createTime).toLocaleDateString() : '—'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
